import { motion } from 'framer-motion'
import { useState } from 'react'
import { worldFaiths } from '@/data/frameworks'
import { cn } from '@/lib/utils'
import { Users, Calendar, BookMarked, Sparkles } from 'lucide-react'

export default function Step2() {
  const [selectedFaith, setSelectedFaith] = useState<string | null>(null)

  const activeFaith = selectedFaith
    ? worldFaiths.find(f => f.id === selectedFaith)
    : null

  return (
    <div className="space-y-12">
      {/* Introduction */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center max-w-3xl mx-auto"
      >
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-gold-400 to-gold-600 mb-6 shadow-divine">
          <span className="text-4xl">🌍</span>
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold text-gold-gradient mb-6">
          World Faith Traditions
        </h1>
        <p className="text-lg text-gold-700/80 dark:text-gold-300/80 leading-relaxed">
          Explore the major religious traditions that have shaped human civilization,
          each offering unique insights into the nature of the divine and the path to spiritual fulfillment.
        </p>
      </motion.div>

      {/* Faith Selector */}
      <div className="flex flex-wrap justify-center gap-4">
        {worldFaiths.map((faith, index) => (
          <motion.button
            key={faith.id}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.1 }}
            onClick={() => setSelectedFaith(selectedFaith === faith.id ? null : faith.id)}
            className={cn(
              'faith-symbol text-4xl transition-all duration-300',
              selectedFaith === faith.id
                ? 'scale-125 ring-4 ring-gold-400 ring-offset-2 dark:ring-offset-divine-50'
                : 'hover:scale-110'
            )}
            style={{
              backgroundColor: selectedFaith === faith.id ? `${faith.color}20` : undefined
            }}
          >
            {faith.symbol}
          </motion.button>
        ))}
      </div>

      {/* Selected Faith Details */}
      <AnimatedFaithCard faith={activeFaith} />

      {/* Faith Cards Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {worldFaiths.map((faith, index) => (
          <motion.div
            key={faith.id}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 + index * 0.1 }}
            onClick={() => setSelectedFaith(faith.id)}
            className={cn(
              'sacred-card p-6 cursor-pointer transition-all duration-300',
              selectedFaith === faith.id && 'ring-2 ring-gold-400 shadow-divine'
            )}
          >
            <div className="flex items-start justify-between mb-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                style={{ backgroundColor: `${faith.color}20` }}
              >
                {faith.symbol}
              </div>
              <div className="text-right">
                <div className="flex items-center gap-1 text-xs text-gold-500">
                  <Calendar className="w-3 h-3" />
                  {faith.founded}
                </div>
              </div>
            </div>

            <h3 className="font-display text-xl font-bold text-gold-800 dark:text-gold-200 mb-2">
              {faith.name}
            </h3>

            <p className="text-sm text-gold-600/70 dark:text-gold-400/70 mb-4 line-clamp-2">
              {faith.description}
            </p>

            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-1 text-gold-500">
                <Users className="w-3 h-3" />
                {faith.followers}
              </div>
              <div className="flex items-center gap-1 text-gold-500">
                <BookMarked className="w-3 h-3" />
                {faith.sacredTexts.length} text{faith.sacredTexts.length > 1 ? 's' : ''}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Interfaith Insight */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="sacred-card p-8 text-center max-w-2xl mx-auto"
      >
        <Sparkles className="w-8 h-8 text-gold-500 mx-auto mb-4" />
        <p className="font-serif text-lg text-gold-800 dark:text-gold-200 italic">
          "The essence of all religions is one. Only their approaches are different."
        </p>
        <footer className="mt-4 text-sm text-gold-600 dark:text-gold-400">
          — Mahatma Gandhi
        </footer>
      </motion.div>
    </div>
  )
}

function AnimatedFaithCard({ faith }: { faith: typeof worldFaiths[0] | null | undefined }) {
  if (!faith) {
    return (
      <div className="sacred-card p-8 text-center">
        <p className="text-gold-600/70 dark:text-gold-400/70">
          Select a faith tradition above to explore its teachings
        </p>
      </div>
    )
  }

  return (
    <motion.div
      key={faith.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="sacred-card p-8 divine-glow"
    >
      <div className="flex flex-col md:flex-row gap-8">
        {/* Left: Symbol and basic info */}
        <div className="text-center md:text-left">
          <div
            className="inline-flex items-center justify-center w-24 h-24 rounded-2xl text-5xl mb-4"
            style={{ backgroundColor: `${faith.color}20` }}
          >
            {faith.symbol}
          </div>
          <h2 className="font-display text-3xl font-bold text-gold-800 dark:text-gold-200 mb-2">
            {faith.name}
          </h2>
          <div className="flex flex-wrap justify-center md:justify-start gap-4 text-sm text-gold-600 dark:text-gold-400">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              Founded {faith.founded}
            </span>
            <span className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              {faith.followers}
            </span>
          </div>
        </div>

        {/* Right: Details */}
        <div className="flex-1 space-y-6">
          <p className="text-gold-700/80 dark:text-gold-300/80 leading-relaxed">
            {faith.description}
          </p>

          {/* Core Beliefs */}
          <div>
            <h4 className="font-display font-semibold text-gold-800 dark:text-gold-200 mb-3">
              Core Beliefs
            </h4>
            <ul className="space-y-2">
              {faith.coreBeliefs.map((belief, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-2 text-sm text-gold-700/70 dark:text-gold-300/70"
                >
                  <span className="text-gold-500 mt-1">✦</span>
                  {belief}
                </motion.li>
              ))}
            </ul>
          </div>

          {/* Sacred Texts */}
          <div>
            <h4 className="font-display font-semibold text-gold-800 dark:text-gold-200 mb-3">
              Sacred Texts
            </h4>
            <div className="flex flex-wrap gap-2">
              {faith.sacredTexts.map((text) => (
                <span
                  key={text}
                  className="px-3 py-1 rounded-full text-sm bg-gold-100 dark:bg-gold-900/30 text-gold-700 dark:text-gold-300"
                >
                  {text}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}
